import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import { PlacementYAnchor } from "building-types/annotation/annotationTypesEnum";
import { TagDirection } from "building-types/foundations/foundationTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { OpeningTagPlacement } from "building-types/openings/openingTagPlacementTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { PlanSettings } from "building-types/settings/planSettings";
import {
  sheetCircleGeometryNew,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import type { SheetPoints } from "common/types/nominalNumbersTypes";
import { percentNew, sheetPointsNew } from "common/utils/nominalNumbers";
import { getOrientedDimLine } from "doc/dimensions";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { WindingDir } from "geom/core";
import { line2MidPt, line2Normal } from "geom/line2";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2EdgeNormal,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Rotate,
} from "geom/polyLine2";
import { polyLine2SetWindingDir, polyLine2Shift, polyLine2WindingDir } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween } from "geom/vec2";
import { generateArrowheadGeometry } from "lib/house/annotation/arrowheadGeometry";

export interface PlanTagCoordinates {
  midLeftX: number;
  midLeftY: number;
  midRightX: number;
  midRightY: number;
  normalX: number;
  normalY: number;
  angle: number;
  centerX: number;
  centerY: number;
  outline: PolyLine2;
}

export function planTagCoordinatesByLine(line: PolyLine2): PlanTagCoordinates {
  const [nX, nY] = polyLine2EdgeNormal(line, 0);

  const outline = getOrientedDimLine(line);

  const angle = vec2AngleBetween(1, 0, outline[2] - outline[0], outline[3] - outline[1]);
  const [normalX, normalY] = [nX, nY];

  const [centerX, centerY] = [0.5 * (line[0] + line[2]), 0.5 * (line[1] + line[3])];

  return {
    midLeftX: line[0],
    midLeftY: line[1],
    midRightX: line[2],
    midRightY: line[3],
    normalX,
    normalY,
    angle,
    centerX,
    centerY,
    outline,
  };
}

function openingTagCoordinatesByPlanOutline(opening: WallOpening): PlanTagCoordinates {
  const { frontRoom, backRoom } = opening;

  let planOutline = wallOpeningPlanExtents(opening);
  // @TODO(Andrew): move this check and reversal into a query
  planOutline = polyLine2Copy(planOutline);
  if (polyLine2WindingDir(planOutline) !== WindingDir.CCW) {
    planOutline = polyLine2Shift(polyLine2SetWindingDir(planOutline, WindingDir.CCW), 1);
  }

  const [midLeftX, midLeftY] = line2MidPt(planOutline[0], planOutline[1], planOutline[6], planOutline[7]);
  const [midRightX, midRightY] = line2MidPt(planOutline[2], planOutline[3], planOutline[4], planOutline[5]);

  const outline = polyLine2ByTwoPoints(midLeftX, midLeftY, midRightX, midRightY);

  const frontInterior = frontRoom && isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure];
  const backInterior = backRoom && isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure];

  if (frontInterior !== backInterior && backInterior) {
    // window direction is flipped INT_EXT
    polyLine2Reverse(outline);
  }

  return planTagCoordinatesByLine(outline);
}

function processOpeningTag(
  text: string | undefined,
  coords: PlanTagCoordinates,
  textRotation: number,
  tagOffset: number,
  process: (text: string, x: number, y: number, normalX: number, normalY: number, textRotation: number) => void,
) {
  if (text) {
    process(
      text,
      coords.centerX - tagOffset * coords.normalX,
      coords.centerY - tagOffset * coords.normalY,
      coords.normalX,
      coords.normalY,
      textRotation,
    );
    return 1;
  }
  return 0;
}

export function placeOpeningTags(
  opening: WallOpening,
  tagText: string | undefined,
  tagDetailText: string | undefined,
  planset: PlanSettings,
  tagPlacement: OpeningTagPlacement,
  process: (text: string, x: number, y: number, normalX: number, normalY: number, textRotationRads: number) => void,
  options: { oppositeWallSide?: boolean } = {},
) {
  // One of the lines may be missing in which case we want to use the first line offset
  const numberOfLines = (tagText ? 1 : 0) + (tagDetailText ? 1 : 0);

  if (opening.planExtents.length === 0) {
    return;
  }

  const coords = openingTagCoordinatesByPlanOutline(opening);
  const { angle: textRotationRads } = coords;

  const { frontRoom, backRoom } = opening;

  const isExterior =
    !(frontRoom && isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure]) ||
    !(backRoom && isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure]);

  const textBoxOffsetSetting = isExterior ? planset.exteriorOpeningTagOffset : planset.interiorOpeningTagOffset;

  // Currently this means the center of the (possibly multiline) textbox. See the TODO below if we have to change that.
  // `oppositeWallSide` flips the tag across the wall from where the door/window tag
  // would normally sit, so two tags on the same opening don't overlap.
  const textBoxOffset = (tagPlacement.tagOffset ?? textBoxOffsetSetting) * (options.oppositeWallSide ? -1 : 1);

  processOpeningTag(
    (tagText ?? "") + (numberOfLines > 1 ? "\n" : "") + (tagDetailText ?? ""),
    coords,
    textRotationRads,
    textBoxOffset,
    process,
  );

  // TODO: if we want the offset to mean the distance of the line that is closer to the wall,
  //       we have to consider the eventual fontsize in world space. We probably want to reinterpret
  //       the tag offset to be in paper space instead.

  // TODO: if we want the tag lines to be independently positioned, we have to consider the normal of the wall,
  //       and which line will be farther from the wall. E.g. on a north facing wall the reported window name
  //       is farther from the wall than the tag detail line, while on a south facing wall it's the other way
  //       around. The code below achieves that line order flipping logic.

  // const firstLineOffsetSetting = isExterior ? planset.exteriorOpeningTagOffset : planset.interiorOpeningTagOffset;

  // const secondLineOffsetSetting = isExterior
  //   ? planset.exteriorOpeningTagDetailOffset
  //   : planset.interiorOpeningTagDetailOffset;

  // const firstLineOffset = (tagPlacement.tagOffsetOverride ?? firstLineOffsetSetting ?? 5);
  // const secondLineOffset = tagPlacement.tagDetailOffsetOverride ?? secondLineOffsetSetting ?? 12;

  // // Offset normal considering the plan rotation. Negative rotation has to be used because of the y-flip.
  // const { normalX, normalY } = openingTagCoordinatesByPlanOutline(opening, -planRotationRads, true);

  // // Due to text directionality the line order may change for the line spacing
  // const flipLineOrder = normalY <= 0 && normalX > -1;

  // const lineOffsets =
  //   numberOfLines > 1 && flipLineOrder ? [secondLineOffset, firstLineOffset] : [firstLineOffset, secondLineOffset];
  // let lineIndex = 0;

  // lineIndex += processOpeningTag(tagText, coords, rotationRadsOnPlan, lineOffsets[lineIndex], process);

  // lineIndex += processOpeningTag(tagDetailText, coords, rotationRadsOnPlan, lineOffsets[lineIndex], process);
}

interface CallOutTagProps {
  topText: string;
  bottomText: string;
  direction: TagDirection;
  /**
   * @deprecated
   */
  planRotationRads?: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  handle?: DocEntityHandle;
  size: number;
}

export function makeCallOutTagGeometry({
  topText,
  bottomText,
  direction,
  planRotationRads = 0,
  startX,
  startY,
  endX,
  endY,
  handle,
  size,
}: CallOutTagProps) {
  const x = startX as SheetPoints;
  const y = startY as SheetPoints;

  const radius = sheetPointsNew((size / 2) * 0.7);

  let [nx, ny] = line2Normal(startX, startY, endX, endY);
  if (direction === TagDirection.LEFT) {
    nx = -nx;
    ny = -ny;
  }

  const circle = sheetCircleGeometryNew({
    x,
    y,
    radius,
    fill: true,
    fillOpacity: percentNew(1),
    fillColor: "white",
    handle,
  });

  const linePl = polyLine2ByTwoPoints(-radius, 0, radius, 0);
  polyLine2Rotate(linePl, -planRotationRads);
  polyLine2Move(linePl, x, y);
  const line = sheetPolylineGeometryNew({
    paths: [linePl],
    handle,
  });

  const halfWidth = size / 2;

  const px = nx * halfWidth;
  const py = ny * halfWidth;

  const rotationRads = Math.atan2(ny, nx) - Math.PI / 2;

  // Generate arrowhead geometry
  const arrowheadGeometry = generateArrowheadGeometry({
    position: [x + px, y + py],
    rotationRads,
    width: size,
    height: halfWidth,
  });

  // Use the arrowhead geometry directly
  const arrow = {
    ...arrowheadGeometry,
    fillColor: "black",
    handle,
  };

  const topTextGeometry = sheetTextGeometryNew({
    text: topText,
    x,
    y,
    placementYAnchor: PlacementYAnchor.BOTTOM,
    rotation: -planRotationRads,
    handle,
  });

  const bottomTextGeometry = sheetTextGeometryNew({
    text: bottomText ?? "",
    x,
    y,
    placementYAnchor: PlacementYAnchor.TOP,
    rotation: -planRotationRads,
    handle,
  });

  const leaderPl = polyLine2ByTwoPoints(startX, startY, endX, endY);
  const leader = sheetPolylineGeometryNew({
    paths: [leaderPl],
    fill: false,
    handle,
  });

  const footLength = size / 2;
  const footPl = polyLine2ByTwoPoints(endX, endY, endX + footLength * nx, endY + footLength * ny);
  const foot = sheetPolylineGeometryNew({
    paths: [footPl],
    linewidth: sheetPointsNew(2.5),
    fill: false,
    handle,
  });

  return { foot, leader, arrow, circle, line, topTextGeometry, bottomTextGeometry };
}
